import * as React from 'react';
import { cn } from '../lib/utils.js';
import { onKeyDownCommitOnEnter } from '../lib/keyboard.js';

// ─── Formatting helpers ───────────────────────────────────────

/** Format a number using Polish locale (1 200,50). */
export function formatPolish(value: number): string {
  return value.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Parse a Polish-formatted number string. Accepts both ',' and '.' as decimal. */
export function parsePolish(text: string): number | null {
  const cleaned = text
    .replace(/\s/g, '') // remove whitespace
    .replace(/\.(?=\d{3}(?:[,.]|$))/g, '') // remove dot-as-thousands-separator (e.g. 1.200,50 or 1.200)
    .replace(',', '.'); // replace decimal comma with dot
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

// ─── Props ────────────────────────────────────────────────────

export interface PriceInputProps {
  /** Current value. null means "not priced" (shows placeholder). */
  value: number | null;
  /** Called on blur with the parsed value, or null if the field was cleared. */
  onChange: (value: number | null) => void;
  /** Shown when value is null. */
  placeholder?: string;
  disabled?: boolean;
  /** Applies destructive border when true (e.g. cost > catalog). */
  error?: boolean;
  /** Currency label appended outside the input. Defaults to 'zł'. */
  currency?: string;
  className?: string;
  /** Forwarded to the inner <input> as data-testid (e.g. "price-input-42"). */
  'data-testid'?: string;
  /** Forwarded to the inner <input> as aria-label for accessibility. */
  'aria-label'?: string;
}

// ─── Debounced commit-on-change (data-loss guard) ─────────────
//
// Under row virtualization (`useTableVirtualizer`) a row carrying a
// mid-edit PriceInput can unmount while the user is still typing — scrolling
// moves it out of the virtualizer's rendered window before `onBlur` ever
// fires, silently dropping the local `rawText`. Committing on a debounced
// change (in addition to the existing onBlur commit) closes that gap: any
// pause in typing persists the value even if the row unmounts moments later.
const COMMIT_DEBOUNCE_MS = 500;

// ─── Style helpers ────────────────────────────────────────────

function borderClass(error: boolean) {
  return error ? 'border-destructive' : 'border-border';
}

function ringClass(error: boolean) {
  return error ? 'focus:ring-destructive' : 'focus:ring-primary';
}

function disabledClass(disabled: boolean) {
  return disabled ? 'opacity-60 cursor-not-allowed bg-muted/40' : 'bg-background';
}

function inputRoundingClass(currency: string | undefined) {
  return currency ? 'rounded-r-none' : 'rounded-r';
}

function resolveAriaLabel(ariaLabel: string | undefined, placeholder: string) {
  return ariaLabel !== undefined ? ariaLabel : placeholder;
}

// ─── Component ───────────────────────────────────────────────

/**
 * Numeric input with Polish locale formatting (1 200,50 zł).
 * Shows formatted value when blurred; raw decimal string when focused.
 * Calls onChange(number | null) on blur.
 */
export function PriceInput({
  value,
  onChange,
  placeholder = '—',
  disabled = false,
  error = false,
  currency = 'zł',
  className,
  'data-testid': dataTestId = 'price-input',
  'aria-label': ariaLabel,
}: PriceInputProps) {
  const [focused, setFocused] = React.useState(false);
  const [rawText, setRawText] = React.useState('');

  const displayValue = focused ? rawText : value !== null ? formatPolish(value) : '';
  const effectiveAriaLabel = resolveAriaLabel(ariaLabel, placeholder);

  function handleFocus() {
    setRawText(value !== null ? String(value).replace('.', ',') : '');
    setFocused(true);
  }

  function handleBlur() {
    setFocused(false);
    onChange(rawText.trim() === '' ? null : (parsePolish(rawText) ?? null));
  }

  // Mirrors the latest render's props/state for the unmount-flush effect
  // below, whose own closure is frozen at mount (empty deps) — never read by
  // the debounce effect itself, which already closes over its own render's
  // fresh values via its own dependency array.
  const latestRef = React.useRef({ rawText, value, onChange });
  latestRef.current = { rawText, value, onChange };

  // Tracks the currently pending debounce timer id, if any. Set only while a
  // debounce is outstanding; the unmount-flush effect below reads it to know
  // whether there is an in-flight edit worth saving. Deliberately NOT cleared
  // by the debounce effect's own cleanup (only `window.clearTimeout`), so a
  // pending id survives regardless of which effect's cleanup React runs first
  // during unmount.
  const pendingTimerIdRef = React.useRef<number | null>(null);

  // Data-loss guard — see COMMIT_DEBOUNCE_MS doc comment above. Skips when
  // the parsed value already matches the committed `value` prop, so a focus
  // + blur with no real edit (or a debounce tick right after the blur commit
  // already landed) never fires a redundant onChange.
  React.useEffect(() => {
    if (!focused) return;
    const timer = window.setTimeout(() => {
      pendingTimerIdRef.current = null;
      const parsed = rawText.trim() === '' ? null : (parsePolish(rawText) ?? null);
      if (parsed === value) return;
      onChange(parsed);
    }, COMMIT_DEBOUNCE_MS);
    pendingTimerIdRef.current = timer;
    return () => window.clearTimeout(timer);
  }, [rawText, focused, value, onChange]);

  // Flushes (commits) any still-pending debounced edit on unmount instead of
  // silently dropping it — e.g. a row scrolling out of the virtualizer's
  // rendered window before the debounce delay elapses. A normal re-render
  // (every keystroke) reschedules a fresh timer via the effect above and
  // never reaches this cleanup, since this effect's own deps never change —
  // only a true unmount does.
  React.useEffect(() => {
    return () => {
      if (pendingTimerIdRef.current === null) return;
      window.clearTimeout(pendingTimerIdRef.current);
      pendingTimerIdRef.current = null;
      const { rawText, value, onChange } = latestRef.current;
      const parsed = rawText.trim() === '' ? null : (parsePolish(rawText) ?? null);
      if (parsed === value) return;
      onChange(parsed);
    };
  }, []);

  return (
    <div data-testid="price-input-wrapper" className={cn('flex items-center min-w-0', className)}>
      <input
        type="text"
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setRawText(e.target.value)}
        onKeyDown={onKeyDownCommitOnEnter}
        onFocus={handleFocus}
        onBlur={handleBlur}
        data-testid={dataTestId}
        aria-label={effectiveAriaLabel}
        className={cn(
          'h-7 min-w-0 flex-1 rounded-l border px-1.5 py-0 text-right font-mono text-[12px]',
          'focus:outline-none focus:ring-1 transition-colors',
          borderClass(error),
          ringClass(error),
          disabledClass(disabled),
          inputRoundingClass(currency),
        )}
      />
      {currency ? (
        <span
          className={cn(
            'h-7 flex items-center px-1.5 rounded-r border border-l-0 text-[10px] font-medium shrink-0 select-none',
            borderClass(error),
            disabled ? 'opacity-60 bg-muted/40 text-muted-foreground/60' : 'text-muted-foreground',
          )}
        >
          {currency}
        </span>
      ) : null}
    </div>
  );
}
